import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from './LanguageContext';
import { apiGet, API_URL, getAdminToken } from '../utils/api';

const LicenseExpiryReport = () => {
  const { print_lang, currentLanguage } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    month: '',
    status: 'all'
  });
  const printRef = useRef();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.month) params.append('month', filters.month);
      if (filters.status) params.append('status', filters.status);

      const response = await apiGet(`/api/admin/reports/license-expiry?${params.toString()}`);
      if (response.success) {
        setData(response.data);
        setSummary(response.summary);
      }
    } catch (err) {
      console.error(err.message || 'Failed to load license expiry report', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      const token = getAdminToken();
      const response = await fetch(`${API_URL}/api/admin/reports/export/license-expiry`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'license_expiry_report.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export report');
    }
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>${print_lang('reports.license', 'License Expiry Report')}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f5f5f5; }
            .summary-grid { display: flex; gap: 20px; margin-bottom: 20px; }
            .summary-card { border: 1px solid #ddd; padding: 15px; flex: 1; text-align: center; }
            .badge { padding: 4px 8px; border-radius: 4px; font-size: 12px; }
            .badge-danger { background: #fee2e2; color: #b91c1c; }
            .badge-warning { background: #fef3c7; color: #b45309; }
            .badge-success { background: #d1fae5; color: #047857; }
            .badge-default { background: #DCE5DF; color: #26332E; }
            h1 { margin-bottom: 20px; }
            @media print { body { -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const getStatusBadge = (daysRemaining, status) => {
    if (status !== 'active') {
      return <span className="badge badge-default">{print_lang('common.inactive', 'Inactive')}</span>;
    }
    if (daysRemaining < 0) {
      return <span className="badge badge-danger">{print_lang('reports.expired', 'Expired')}</span>;
    }
    if (daysRemaining <= 30) {
      return <span className="badge badge-warning">{print_lang('reports.expiringSoon', 'Expiring Soon')}</span>;
    }
    return <span className="badge badge-success">{print_lang('reports.active', 'Active')}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(currentLanguage === 'th' ? 'th-TH' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Generate month options for filter
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const value = date.toISOString().slice(0, 7);
      const label = date.toLocaleDateString(currentLanguage === 'th' ? 'th-TH' : 'en-US', {
        year: 'numeric',
        month: 'long'
      });
      options.push({ value, label });
    }
    return options;
  };

  return (
    <div>
      {/* Filters */}
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: '200px' }}>
            <label className="form-label">{print_lang('reports.expiryMonth', 'Expiry Month')}</label>
            <select
              className="form-select"
              value={filters.month}
              onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
            >
              <option value="">{print_lang('reports.allMonths', 'All Months')}</option>
              {getMonthOptions().map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">{print_lang('reports.status', 'Status')}</label>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              {[
                { id: 'all', label: print_lang('common.all', 'All') },
                { id: 'expired', label: print_lang('reports.expired', 'Expired') },
                { id: 'expiring', label: print_lang('reports.expiring', 'Expiring') },
                { id: 'active', label: print_lang('reports.active', 'Active') }
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setFilters(prev => ({ ...prev, status: opt.id }))}
                  className={`btn btn-sm ${filters.status === opt.id ? 'btn-primary' : 'btn-outline'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
            <button
              type="button"
              className="btn btn-outline"
              onClick={handlePrint}
              disabled={loading}
              style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              {print_lang('reports.print', 'Print')}
            </button>
            <button
              type="button"
              className="btn btn-success"
              onClick={handleExport}
              disabled={loading}
              style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {print_lang('reports.exportCsv', 'Export CSV')}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card">
          <div className="loading-container">
            <div className="spinner" />
          </div>
        </div>
      ) : (
        <div ref={printRef}>
          <h2 style={{ marginBottom: '1rem' }}>{print_lang('reports.license', 'License Expiry Report')}</h2>

          {/* Summary Cards */}
          {summary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
              <div className="card" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{print_lang('reports.expired', 'Expired')}</div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{summary.expired || 0}</div>
              </div>
              <div className="card" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{print_lang('reports.expiringSoon', 'Expiring Soon')}</div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{summary.expiring_soon || 0}</div>
              </div>
              <div className="card" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{print_lang('reports.active', 'Active')}</div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{summary.active || 0}</div>
              </div>
              {/* Ramp stops are written out rather than var(--brand-*): this subtree is
                  cloned into a bare print window with no theme.css, where the custom
                  properties resolve to nothing and leave white text on a blank card. */}
              <div className="card" style={{ background: 'linear-gradient(135deg, #2F6B58 0%, #20463B 100%)', color: 'white', textAlign: 'center' }}>
                <div style={{ fontSize: '0.875rem', opacity: 0.9 }}>{print_lang('reports.total', 'Total')}</div>
                <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{summary.total || 0}</div>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="card">
            {data.length === 0 ? (
              <div className="empty-state">
                <h3 className="empty-state-title">{print_lang('common.noData', 'No data found')}</h3>
              </div>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>{print_lang('admin.name', 'Admin Name')}</th>
                      <th>{print_lang('admin.email', 'Email')}</th>
                      <th>{print_lang('admin.licenseExpiry', 'License Expiry')}</th>
                      <th style={{ textAlign: 'center' }}>{print_lang('reports.daysRemaining', 'Days Remaining')}</th>
                      <th style={{ textAlign: 'center' }}>{print_lang('branch.branches', 'Branches')}</th>
                      <th style={{ textAlign: 'center' }}>{print_lang('reports.users', 'Users')}</th>
                      <th style={{ textAlign: 'center' }}>{print_lang('reports.status', 'Status')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((row, index) => (
                      <tr key={index}>
                        <td><strong>{row.name}</strong></td>
                        <td>{row.email}</td>
                        <td>{formatDate(row.license_expired)}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span style={{
                            color: row.days_until_expiry < 0 ? '#b91c1c' :
                                   row.days_until_expiry <= 30 ? '#b45309' : '#047857',
                            fontWeight: 'bold'
                          }}>
                            {row.days_until_expiry}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>{row.branch_count || 0}</td>
                        <td style={{ textAlign: 'center' }}>{row.user_count || 0}</td>
                        <td style={{ textAlign: 'center' }}>{getStatusBadge(row.days_until_expiry, row.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LicenseExpiryReport;
